/**
 * The consent lines an organizer writes for the last page of a booking (batch-29, item PK5), the part with no screen.
 * An event with no lines keeps the one fixed tick, "I agree to the terms". With lines, each is a tick of its own, required
 * or not; a booking is sent with the ids of the lines ticked, the server refuses it while a required one is missing, and
 * stores what was ticked with the wording as it was. Every text is shown as text.
 */
export interface ConsentLine { id: string; text: string; required: boolean }
/** A line as the organizer's card holds it and as events.consent_lines stores it. */
export interface ConsentDef { text: string; required: boolean }
/** One stored answer of a booking: the wording as the guest saw it, and whether it was ticked. */
export interface ConsentAnswer { text: string; required: boolean; agreed: boolean }

export const CONSENT_LIMITS = { lines: 10, text: 500 } as const;

/** The event's lines as the page shows them, in the organizer's order. Anything that is not a line is left out; a missing key means none. */
export function consentsOf(event: { consents?: unknown } | null | undefined): ConsentLine[] {
  const raw = Array.isArray(event?.consents) ? event.consents : [];
  const out: ConsentLine[] = [];
  for (const c of raw as Array<Record<string, unknown> | null>) {
    if (!c || typeof c.id !== 'string' || c.id === '' || typeof c.text !== 'string' || c.text.trim() === '') continue;
    if (out.some((o) => o.id === c.id)) continue;
    out.push({ id: c.id, text: c.text, required: c.required === true });
  }
  return out;
}

/** True when every required line is ticked. No lines: true (the fixed tick is the page's own). */
export const consentGiven = (lines: readonly ConsentLine[], ticked: readonly string[]): boolean => lines.every((l) => !l.required || ticked.includes(l.id));

/** The ids sent with a booking: the ticked lines that are still on the page, in the page's order. */
export const consentIds = (lines: readonly ConsentLine[], ticked: readonly string[]): string[] => lines.filter((l) => ticked.includes(l.id)).map((l) => l.id);

/** The stored lines as the organizer's card edits them. Anything that is not a line is left out. */
export function consentDefs(stored: unknown): ConsentDef[] {
  if (!Array.isArray(stored)) return [];
  return (stored as Array<Record<string, unknown> | null>).flatMap((c) => (c && typeof c.text === 'string' ? [{ text: c.text, required: c.required === true }] : []));
}

/** What is saved: each text trimmed. */
export const consentToStore = (rows: readonly ConsentDef[]): ConsentDef[] => rows.map((r) => ({ text: r.text.trim(), required: r.required }));

export type ConsentProblem = 'count' | 'empty' | 'long' | 'twice';
export const CONSENT_PROBLEMS: Record<ConsentProblem, string> = {
  count: `Use at most ${CONSENT_LIMITS.lines} consent lines.`,
  empty: 'Write the words of every consent line, or remove it.',
  long: `A consent line is at most ${CONSENT_LIMITS.text} characters.`,
  twice: 'Two consent lines say the same thing. Change one or remove it.',
};
/** Why the lines cannot be saved, or null. The database refuses the same things (events_consent_lines_check). */
export function consentProblem(rows: readonly ConsentDef[]): ConsentProblem | null {
  const texts = rows.map((r) => r.text.trim());
  if (rows.length > CONSENT_LIMITS.lines) return 'count';
  if (texts.some((t) => t === '')) return 'empty';
  if (texts.some((t) => t.length > CONSENT_LIMITS.text)) return 'long';
  if (new Set(texts).size !== texts.length) return 'twice';
  return null;
}

/**
 * The recommended payment terms an organizer can add with one click: required lines that, ticked and stored word for word
 * with the booking, show what the guest agreed to about the charge, cancellations, no-shows and disputes.
 */
export const RECOMMENDED_PAYMENT_TERMS: ConsentDef[] = [
  { text: 'I authorize the organizer to charge my card the total shown, including any deposit, any later balance, and any change I make to this reservation.', required: true },
  { text: 'I have read the cancellation and refund terms for this event and I accept them. There are no refunds outside those terms.', required: true },
  { text: 'If I do not arrive, or I leave early, I will be charged as those terms say.', required: true },
  { text: 'If I have a question about a charge, I will contact the organizer first and give them a chance to resolve it before I dispute the charge with my bank.', required: true },
  { text: 'The name on this reservation and the details I gave are accurate, and I am the cardholder or I am authorized to use this card.', required: true },
];

/** The rows with every recommended line they do not already have (by exact text) added at the end, as many as fit under the line limit. */
export function withRecommendedTerms<T extends ConsentDef>(rows: readonly T[], make: (line: ConsentDef) => T = (l) => ({ ...l }) as T): T[] {
  const have = new Set(rows.map((r) => r.text.trim()));
  const room = Math.max(0, CONSENT_LIMITS.lines - rows.length);
  const add = RECOMMENDED_PAYMENT_TERMS.filter((l) => !have.has(l.text)).slice(0, room).map(make);
  return [...rows, ...add];
}

/** A booking's stored answers, as the organizer reads them. Anything that is not an answer is left out. */
export function consentAnswers(stored: unknown): ConsentAnswer[] {
  if (!Array.isArray(stored)) return [];
  return (stored as Array<Record<string, unknown> | null>).flatMap((c) =>
    c && typeof c.text === 'string' && c.text !== '' ? [{ text: c.text, required: c.required === true, agreed: c.agreed === true }] : []);
}
